import React, { useCallback, useState } from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import type { ExtNode } from 'relatives-tree/lib/types';
import { green, grey } from '@mui/material/colors';
import { useTheme } from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { getYearFromDate } from '../../../utils/date';
import { MemberType, Person } from '../../../types/tree';
import { stringAvatar } from '../../../utils/transformTree';

interface FamilyNodeProps {
  node: ExtNode;
  isRoot: boolean;
  isHover?: boolean;
  isSelected?: boolean;
  onClick?: (id: string) => void;
  onSubClick: (id: string) => void;
  onQuickAdd?: (relation: MemberType, person?: Person) => void;
  style?: React.CSSProperties;
  nodeDetails?: Person;
}

export const FamilyNode = React.memo(function FamilyNode({
  node,
  isRoot,
  isSelected,
  onClick,
  onSubClick,
  onQuickAdd,
  style,
  nodeDetails,
}: FamilyNodeProps) {
  const { palette, typography } = useTheme();
  const { t } = useTranslation('tree');
  const [isHovered, setIsHovered] = useState(false);

  const clickHandler = useCallback(() => {
    if (onClick) {
      onClick(node.id);
    }
  }, [node.id, onClick]);

  const clickSubHandler = useCallback(
    () => onSubClick(node.id),
    [node.id, onSubClick],
  );

  const hasFather = Boolean(nodeDetails?.father_id);
  const hasMother = Boolean(nodeDetails?.mother_id);
  const showHandles = isHovered || isSelected;
  const isDeceased = nodeDetails?.is_alive === false;
  const isRootHighlight = isRoot && !isDeceased;
  const fullName = [nodeDetails?.first_name, nodeDetails?.last_name]
    .filter(Boolean)
    .join(' ');

  return (
    <Box
      sx={{
        position: 'absolute',
        display: 'flex',
        p: 2,
      }}
      style={style}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          gap: 1.5,
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: isDeceased ? grey[100] : isRoot ? green[500] : grey[300],
          borderRadius: 6,
          border: `2px solid ${isDeceased ? grey[500] : 'transparent'}`,
          boxSizing: 'border-box',
          color: isRoot && !isDeceased ? 'white' : palette.text.primary,
          overflow: 'hidden',
          cursor: 'pointer',
          position: 'relative',
          pb: showHandles && onQuickAdd ? 3 : 2,
          pt: 2,
          '&:hover': {
            borderColor: grey[700],
          },
        }}
        onClick={clickHandler}
      >
        {/* Empty-state prompts when missing parent */}
        {(!hasFather || !hasMother) && onQuickAdd && (
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              justifyContent: 'center',
              flexWrap: 'wrap',
              px: 1,
              width: '100%',
            }}
          >
            {!hasFather && (
              <Tooltip title="Add father" arrow>
                <Box
                  component="button"
                  type="button"
                  data-testid={`add-father-${node.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuickAdd(MemberType.FATHER, nodeDetails);
                  }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    px: 0.75,
                    py: 0.2,
                    fontSize: '10px',
                    fontWeight: 600,
                    borderRadius: '10px',
                    border: '1px dashed',
                    borderColor: isRootHighlight
                      ? 'rgba(255,255,255,0.8)'
                      : palette.primary.main,
                    color: isRootHighlight ? 'white' : palette.primary.main,
                    backgroundColor: isRootHighlight
                      ? 'rgba(255,255,255,0.1)'
                      : 'rgba(25,118,210,0.06)',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: isRootHighlight
                        ? 'rgba(255,255,255,0.25)'
                        : 'rgba(25,118,210,0.15)',
                    },
                  }}
                >
                  <AddIcon sx={{ fontSize: 12 }} /> Father
                </Box>
              </Tooltip>
            )}
            {!hasMother && (
              <Tooltip title="Add mother" arrow>
                <Box
                  component="button"
                  type="button"
                  data-testid={`add-mother-${node.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuickAdd(MemberType.MOTHER, nodeDetails);
                  }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    px: 0.75,
                    py: 0.2,
                    fontSize: '10px',
                    fontWeight: 600,
                    borderRadius: '10px',
                    border: '1px dashed',
                    borderColor: isRootHighlight
                      ? 'rgba(255,255,255,0.8)'
                      : palette.text.secondary,
                    color: isRootHighlight ? 'white' : palette.text.secondary,
                    backgroundColor: isRootHighlight
                      ? 'rgba(255,255,255,0.1)'
                      : 'rgba(78, 57, 81, 0.06)',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: isRootHighlight
                        ? 'rgba(255,255,255,0.25)'
                        : 'rgba(78, 57, 81, 0.15)',
                    },
                  }}
                >
                  <AddIcon sx={{ fontSize: 12 }} /> Mother
                </Box>
              </Tooltip>
            )}
          </Box>
        )}

        <Avatar
          src={nodeDetails?.photo_url}
          alt={
            fullName ? `${fullName} portrait` : t('nodeStatus.memberPortrait')
          }
          {...stringAvatar(nodeDetails)}
        />
        <Box
          p={1}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography
            sx={{
              fontSize: {
                xs: typography.body2.fontSize,
                md: typography.body1.fontSize,
              },
              fontWeight: typography.h1.fontWeight,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'center',
            }}
            title={fullName}
          >
            {fullName}
          </Typography>
          {isDeceased && (
            <Chip
              size="small"
              label={t('nodeStatus.deceased')}
              role="status"
              aria-label={t('nodeStatus.deceased')}
              sx={{
                height: 22,
                maxWidth: '100%',
                color: grey[800],
                backgroundColor: grey[200],
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.04em',
                '& .MuiChip-label': {
                  px: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
          )}
          <Typography
            sx={{
              fontSize: {
                xs: typography.caption.fontSize,
                md: typography.body2.fontSize,
              },
              textAlign: 'center',
              color: isDeceased ? palette.text.secondary : 'inherit',
            }}
          >
            {nodeDetails?.date_of_birth
              ? `${getYearFromDate(nodeDetails.date_of_birth)}${
                  isDeceased && nodeDetails.death_date
                    ? ` - ${getYearFromDate(nodeDetails.death_date)}`
                    : ''
                }`
              : ''}
          </Typography>
        </Box>
        {isRoot && (
          <Box
            sx={{
              bgcolor: palette.info.main,
              color: palette.primary.contrastText,
              px: 1,
              borderRadius: 1,
            }}
          >
            <Typography sx={{ fontSize: '10px' }} textAlign="center">
              Focus Person
            </Typography>
          </Box>
        )}

        {/* On-node "+" quick-add handles on hover/select */}
        {showHandles && onQuickAdd && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 6,
              left: 4,
              right: 4,
              display: 'flex',
              gap: 0.2,
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: 'rgba(30, 41, 59, 0.92)',
              borderRadius: 3,
              p: 0.5,
              zIndex: 10,
              boxShadow: 2,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {!hasFather && (
              <Tooltip title="Add father" arrow>
                <IconButton
                  size="small"
                  aria-label="Add father"
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuickAdd(MemberType.FATHER, nodeDetails);
                  }}
                  sx={{ color: '#fff', p: 0.4 }}
                >
                  <AddIcon sx={{ fontSize: 14 }} />
                  <Typography sx={{ fontSize: '9px', ml: 0.2 }}>Fa</Typography>
                </IconButton>
              </Tooltip>
            )}
            {!hasMother && (
              <Tooltip title="Add mother" arrow>
                <IconButton
                  size="small"
                  aria-label="Add mother"
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuickAdd(MemberType.MOTHER, nodeDetails);
                  }}
                  sx={{ color: '#fff', p: 0.4 }}
                >
                  <AddIcon sx={{ fontSize: 14 }} />
                  <Typography sx={{ fontSize: '9px', ml: 0.2 }}>Mo</Typography>
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Add partner/spouse" arrow>
              <IconButton
                size="small"
                aria-label="Add partner"
                onClick={(e) => {
                  e.stopPropagation();
                  onQuickAdd(MemberType.SPOUSE, nodeDetails);
                }}
                sx={{ color: '#fff', p: 0.4 }}
              >
                <AddIcon sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: '9px', ml: 0.2 }}>
                  Spouse
                </Typography>
              </IconButton>
            </Tooltip>
            <Tooltip title="Add sibling" arrow>
              <IconButton
                size="small"
                aria-label="Add sibling"
                onClick={(e) => {
                  e.stopPropagation();
                  onQuickAdd(MemberType.SIBLING, nodeDetails);
                }}
                sx={{ color: '#fff', p: 0.4 }}
              >
                <AddIcon sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: '9px', ml: 0.2 }}>
                  Sibling
                </Typography>
              </IconButton>
            </Tooltip>
            <Tooltip title="Add child" arrow>
              <IconButton
                size="small"
                aria-label="Add child"
                onClick={(e) => {
                  e.stopPropagation();
                  onQuickAdd(MemberType.CHILD, nodeDetails);
                }}
                sx={{ color: '#fff', p: 0.4 }}
              >
                <AddIcon sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: '9px', ml: 0.2 }}>Child</Typography>
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
      {node.hasSubTree && (
        <Box
          sx={{
            position: 'absolute',
            top: '6px',
            right: '24px',
            width: '14px',
            height: '10px',
          }}
        >
          <Tooltip title="View sub-tree" arrow>
            <IconButton
              onClick={clickSubHandler}
              sx={{
                backgroundColor: palette.info.main,
                color: palette.common.white,
                '&:hover': {
                  backgroundColor: palette.info.dark,
                },
              }}
            >
              <AccountTreeIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
});
